import { sqWorkbenchStore, sqWorkbookStore, sqWorksheetStore, sqWorkstepsStore } from '@/core/core.stores';
import { formatTime } from '@/datetime/dateTime.utilities';
import { capturesEnabled } from '@/services/systemConfiguration.utilities';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { ViewTypeEnum } from '@seeqdev/api/model/CaptureInputV1';
import type { Moment } from 'moment';
import type { WorksheetViewType } from '@/worksheet/worksheet.constants';
import type { CaptureInputV1 } from '@seeqdev/api/model/CaptureInputV1';

/** Everything a capture records about where it was taken. */
export type CaptureSource = Omit<CaptureInputV1, 'axes' | 'description'>;

/** The title a capture carries when nobody types one. */
export function capturePlaceholderTitle(workbookName: string, worksheetName?: string): string {
  return worksheetName ? `${workbookName} - ${worksheetName}` : workbookName;
}

/** The CAPTURE-n a person refers to a capture by. */
export function captureLabel(number: number): string {
  return `CAPTURE-${number}`;
}

const STAMP_DATE_FORMAT = 'MMM D YYYY';
const STAMP_DATE_TIME_FORMAT = 'MMM D YYYY HH:mm';

/** The day a capture was taken, as the picture's stamp writes it in the worksheet's time zone. */
export function captureStampDate(time: Moment | number | string, timezone: { name: string }): string {
  return formatTime(time, timezone, STAMP_DATE_FORMAT);
}

/** The visible time range the picture's stamp carries, in the time zone the chart's x axis uses. */
export function captureStampRange(
  range: { start: Moment | number; end: Moment | number },
  timezone: { name: string },
): { start: string; end: string; zone: string } {
  return {
    start: formatTime(range.start, timezone, STAMP_DATE_TIME_FORMAT),
    end: formatTime(range.end, timezone, STAMP_DATE_TIME_FORMAT),
    zone: formatTime(range.end, timezone, 'z'),
  };
}

/** The `viewType` a capture from this view carries, or undefined when the view cannot be captured. */
export function captureViewType(viewKey: WorksheetViewType): ViewTypeEnum | undefined {
  switch (viewKey) {
    case WORKSHEET_VIEW.TREND:
      return ViewTypeEnum.Trend;
    case WORKSHEET_VIEW.SCATTER_PLOT:
      return ViewTypeEnum.Scatter;
    case WORKSHEET_VIEW.TABLE:
      return ViewTypeEnum.Table;
    case WORKSHEET_VIEW.TREEMAP:
      return ViewTypeEnum.Treemap;
    default:
      return undefined;
  }
}

/**
 * Where a capture taken right now would come from, or undefined when this worksheet cannot be captured. The stores
 * are read on the call, so the answer describes the worksheet at the moment of the press.
 */
export function currentCaptureSource(): CaptureSource | undefined {
  const viewType = captureViewType(sqWorksheetStore.view.key);
  const workstepId = sqWorkstepsStore.getState().current.id;

  if (!viewType || !workstepId) {
    return undefined;
  }

  const { workbookId, worksheetId } = sqWorkbenchStore.stateParams;

  return {
    workbookId,
    worksheetId,
    workstepId,
    viewType,
    title: capturePlaceholderTitle(sqWorkbookStore.name, sqWorkbookStore.getWorksheetName(worksheetId)),
  };
}

/** Whether the captures management page and the links to it exist for this user. */
export const shouldOfferCapturesManagement = (isTrusted: boolean): boolean => capturesEnabled() && isTrusted;
